import {getPrefixedUrlOptions} from 'common/util/url';
import {Results as GetFeaturesResults} from 'app_api/projects/features/types';
import {getUserIdentityFromCookie} from 'common/report/util/user-identity-from-cookie';
import {DEFAULT_TTL} from 'common/report/queries/utils/fetch-cache';

import {
  FeatureGatesValue,
  LocalStorageKey,
  ProjectFeatureFlag,
  RestrictedReport,
  RestrictEntitySaving,
  UserFeatureFlag,
} from '../constants';
import {BaseClient, QueryOptions} from '../queries/utils/base-client';
import {isStandalone} from '../util/env';
import {getMpReportGlobals} from '../util/mp-report-globals';
import {Persistence, UserPersistence} from '../persistence';
import {Store} from './store';

export enum FetchOption {
  LocalStorage = `local_storage`,
  MpGlobals = `mp_globals`,
  Api = `api`,
  AlreadyPopulated = `already_populated`,
  FetchFailed = `fetch_failed`,
}

interface FeaturesClientResults extends GetFeaturesResults {
  fetchSource: FetchOption;
}

export class Features {
  private projectFeatureFlags: Set<string>;
  private projectFeatureGatePlanName: string;
  private projectFeatureGatesValue: FeatureGatesValue;
  private userFeatureFlags: Set<string>;
  private restrictedReports: Set<string>;
  private restrictEntitySavings: Set<string>;

  public fetchSource: FetchOption;

  constructor(results: FeaturesClientResults) {
    this.projectFeatureFlags = new Set([...results.project_feature_flags, ...getPrefixedUrlOptions(`feature`)]);
    this.projectFeatureGatePlanName = results.project_feature_gate_name;
    this.projectFeatureGatesValue = results.project_feature_gates as FeatureGatesValue;
    this.restrictedReports = new Set(results.project_feature_gates.restricted_reports);
    this.restrictEntitySavings = new Set(results.project_feature_gates.restrict_entity_saving);
    this.userFeatureFlags = new Set(results.user_feature_flags);
    this.fetchSource = results.fetchSource;
  }

  hasProjectFeatureFlag(name: ProjectFeatureFlag) {
    return this.projectFeatureFlags.has(name);
  }

  hasUserFeatureFlag(name: UserFeatureFlag) {
    return this.userFeatureFlags.has(name);
  }

  hasAccessToReport(name: RestrictedReport) {
    return !this.restrictedReports.has(name);
  }

  canSaveEntity(name: RestrictEntitySaving) {
    return !this.restrictEntitySavings.has(name);
  }

  getFeatureGate<K extends keyof FeatureGatesValue>(name: K): FeatureGatesValue[K] {
    return this.projectFeatureGatesValue[name];
  }

  getFeatureGatePlanName(): string {
    return this.projectFeatureGatePlanName;
  }
}

class FeaturesClient extends BaseClient {
  private persistence = new UserPersistence<GetFeaturesResults>({
    namespace: LocalStorageKey.FeaturesCache,
    userId: getUserIdentityFromCookie()?.id,
    ttlMs: DEFAULT_TTL * 1000,
  });

  buildEndpoint({projectId}) {
    return projectId ? `api/app/projects/${projectId}/features` : `api/app/projects/features`;
  }

  /**
   * since featuresStore blocks iron apps UI initialization we want query() to minimize UI blocking time.
   * 1) if mp.report.globals has features related properties, then use that and avoid network call
   * 2) if we have to make api call, store the response in localStorage instead of fetch-cache.
   *    localStorage is synchronous and faster than fetch-cache for small chunks of data.
   */
  async query(params: {projectId: number}, queryOpts: QueryOptions): Promise<FeaturesClientResults | null> {
    if (!isStandalone()) {
      const mpReportGlobals = getMpReportGlobals();
      const featuresResults: FeaturesClientResults = {
        project_feature_gate_name: mpReportGlobals.feature_gate_name,
        project_feature_gates: mpReportGlobals.feature_gates,
        project_feature_flags: mpReportGlobals.project_featureflags,
        user_feature_flags: mpReportGlobals.user_feature_flags,
        fetchSource: FetchOption.MpGlobals,
      };
      if (Object.values(featuresResults).every((val) => val !== undefined)) {
        return Promise.resolve(featuresResults);
      }
    }

    if (queryOpts.clearCache) {
      this.persistence.remove(LocalStorageKey.FeaturesCache);
    }

    if (queryOpts.fromFetchCache) {
      const results = this.persistence.get(params.projectId);
      if (results) {
        return {...results, fetchSource: FetchOption.LocalStorage};
      }

      return null;
    }

    // override queryOpts since we're using localStorage instead of fetch-cache
    const results = await super.query(params, {...queryOpts, fromFetchCache: false, updateFetchCache: false});

    if (queryOpts.updateFetchCache) {
      this.persistence.set(params.projectId, results);
    }

    return {...results, fetchSource: FetchOption.Api};
  }

  processResponse(response) {
    if (response.error) {
      throw new Error(response.error);
    }

    return response.results;
  }
}

// TODO(jakub) 11/9/2020 - leave this running for a few weeks
// to clean user storage since switching to UserPersistence
Persistence.purge([LocalStorageKey.FeaturesCache]);

export const featuresClient = new FeaturesClient();

export const featuresStore = new Store<Features>({
  apiClient: featuresClient,
  defaultValue: null,
  name: `features`,
  transform: {index: (results: FeaturesClientResults) => new Features(results)},
});
